import { isApiError } from '@multaqa/api-client'
import { DEFAULT_LOCALE, isLocale } from '@multaqa/i18n'
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { AppShell } from '@/components/app-shell'
import { NotFoundScreen } from '@/components/not-found-screen'
import { currentUserQuery } from '@/lib/auth'
import { applyStoredLocaleOnce } from '@/lib/locale'
import { ensureActiveCompany, type Me, meQuery } from '@/lib/me'
import { isSelfServiceOnly } from '@/lib/rail-visibility'

/** Account controls remain available to a session with own-scope grants only. */
const SELF_SERVICE_ACCOUNT_PATHS = ['/account', '/notifications', '/two-factor-setup']

/**
 * Pathless layout route enforcing the session.
 *
 * Everything nested under `_authenticated/` is behind this guard, so a new
 * screen is protected by where its file sits rather than by remembering to
 * add a check. `beforeLoad` runs before the component, so an unauthenticated
 * visitor never renders a frame of the shell.
 *
 * This is a UX guard, not a security boundary. The API authorises every
 * request independently; a user who defeats this sees an empty shell and
 * 401s.
 *
 * The `/me` query is prefetched here alongside `/user`. The shell shows the
 * active company code in the top bar, and that code comes from `/me` —
 * prefetching avoids a frame of "—" between the guard returning and the
 * switcher fetching. `/me` may 401 on its own (the spec calls it out); both
 * 401s go through the same `onUnauthorized` in `@multaqa/api-client`, so
 * the guard's catch still covers it.
 *
 * `ensureActiveCompany` then makes sure the session actually has a company
 * scope on company-scoped routes. The backend never picks one (MeApiTest: "no
 * fallback, no guess"), so a fresh sign-in arrives with `active_company_id:
 * null` and no effective grants — which rendered a rail containing nothing but
 * Dashboard. Choosing here rather than in an effect means the shell's first
 * render already has grants. See `lib/me.ts` for why the choice belongs to the
 * client.
 *
 * Group reporting is deliberately the exception: it reads across companies and
 * must not acquire or change the active company just because its URL was opened.
 */
export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ context, location, cause }) => {
    // Search-only navigations keep this layout mounted. Re-running the async
    // auth bootstrap here makes a filter click wait on /user and /me again;
    // while that transition is pending the old URL and open control remain on
    // screen, which looks like the filter froze. The API still authorizes the
    // filtered request, and a 401 redirects through the client middleware.
    if (cause === 'stay') return

    // Both requests at once: one after the other doubled the wait before the
    // first screen. The results are still read in order, so a /user failure
    // wins over a /me failure exactly as before.
    const [user, meResult] = await Promise.allSettled([
      context.queryClient.ensureQueryData(currentUserQuery),
      context.queryClient.ensureQueryData(meQuery),
    ])
    const guardError = (reason: unknown) =>
      isApiError(reason) && reason.status === 401
        ? redirect({ to: '/login', search: { redirect: location.href } })
        : reason
    if (user.status === 'rejected') throw guardError(user.reason)
    if (meResult.status === 'rejected') throw guardError(meResult.reason)
    const me: Me = meResult.value

    // Captured, not discarded. `ensureActiveCompany` returns the REFRESHED
    // `Me`, and the grant list on the stale one was computed before a company
    // was chosen — reading it would test the empty shape every time.
    const resolved = location.pathname.startsWith('/group/')
      ? me
      : await ensureActiveCompany(context.queryClient, me)

    // One test in `beforeLoad` covers every deep link, `/hr/employees`
    // included. Only account controls are exempt from the app-download page.
    if (
      isSelfServiceOnly(resolved) &&
      !SELF_SERVICE_ACCOUNT_PATHS.some(
        (prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`),
      )
    ) {
      throw redirect({ to: '/get-the-app' })
    }

    // An explicit choice made on a guest screen is already in localStorage.
    // `applyStoredLocaleOnce` preserves it; the server locale is only the
    // fallback for a browser with no local preference.
    applyStoredLocaleOnce(isLocale(me.user.locale) ? me.user.locale : DEFAULT_LOCALE)
  },

  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),

  notFoundComponent: () => <NotFoundScreen />,
})
